import { Box, Button, Grid, Typography } from "@mui/material";
import React, { FC, useState, useCallback, useRef } from "react";
import Map, {
  Source,
  Layer,
  Marker,
  LayerProps,
  MapRef,
  NavigationControl,
  FullscreenControl,
  GeolocateControl,
  Popup,
  ScaleControl,
} from "react-map-gl/mapbox";
import { IGeoLocatorCoordinatesParams } from "./GeoLocator";
import type { FeatureCollection } from "geojson";
import { H4 } from "../../Typography";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FlexBox from "../../FlexBox";
import { GEO_LOCATION_TOKEN } from "../../../api/constants";
import { SearchBox } from "@mapbox/search-js-react";
import uniqueId from "../../../utils/generateId";
import PlaceIcon from "@mui/icons-material/Place";

import mapboxgl from "mapbox-gl";
// prettier-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

interface IGeoLocatorMapProps {
  setLocation?: (value: any) => void;
  initialLocation: IGeoLocatorCoordinatesParams;
  setViewport?: (value: any) => void;
  closeDrawer?: () => void;
  editable?: boolean;
  viewLocationsList?: boolean;
  locations?: IGeoLocatorCoordinatesParams[];
  zoom?: number;
}

const layerStyle: LayerProps = {
  id: "point",
  type: "circle",
  paint: {
    "circle-radius": 10,
    "circle-color": "#007cbf",
  },
};

interface IViewStateProps {
  longitude: number;
  latitude: number;
  zoom?: number;
}

export const GeoLocatorMap: FC<IGeoLocatorMapProps> = ({
  setLocation,
  initialLocation,
  closeDrawer,
  editable,
  locations,
  viewLocationsList,
  zoom,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [showPopup, setShowPopup] = useState<boolean>(true);
  let mapRef = useRef<MapRef>(null);
  const [viewState, setViewState] = React.useState<IViewStateProps>({
    longitude: initialLocation?.longitude || 0,
    latitude: initialLocation?.latitude || 0,
  });
  const [refreshId] = useState<any>(uniqueId);

  const [currentLocation, setCurrentLocation] = React.useState<IViewStateProps>(
    {
      longitude: 0,
      latitude: 0,
    },
  );

  const geojson: FeatureCollection = {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [viewState?.longitude, viewState?.latitude],
        },
        properties: {},
      },
    ],
  };

  React.useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });

        if (initialLocation?.latitude && initialLocation?.latitude) {
          setViewState({
            longitude: initialLocation?.longitude,
            latitude: initialLocation?.latitude,
          });
        } else {
          setViewState({
            longitude: position.coords.longitude,
            latitude: position.coords.latitude,
          });
        }
      });
    }
  }, [refreshId]);

  const handleCloseDrawer = () => {
    setLocation?.({
      longitude: 0,
      latitude: 0,
    } as IGeoLocatorCoordinatesParams);
    closeDrawer?.();
  };

  function CurrentLocationNavigateButton() {
    const onClick = () => {
      setViewState(currentLocation);
      setLocation?.(currentLocation);
      closeDrawer?.();
    };

    return (
      <Box>
        <Button
          onClick={onClick}
          variant="contained"
          color={"info"}
          sx={{
            padding: 0.5,
            px: 1,
            fontSize: 10,
            color: "#fff",
          }}
        >
          <LocationOnIcon sx={{ fontSize: 16 }} />
          Use Current Location
        </Button>
      </Box>
    );
  }

  function SaveLocationButton() {
    const onClick = () => {
      setLocation?.({
        longitude: viewState?.longitude,
        latitude: viewState?.latitude,
      });
      closeDrawer?.();
    };

    return (
      <Box>
        <Button
          onClick={onClick}
          variant="contained"
          sx={{ padding: 0.5, px: 1, fontSize: 10 }}
        >
          Save & Close
        </Button>
      </Box>
    );
  }

  function ClearCurrentLocationButton() {
    const handleReset = () => {
      if (initialLocation?.latitude !== 0 || initialLocation?.longitude !== 0) {
        setLocation?.({
          longitude: initialLocation?.longitude,
          latitude: initialLocation?.latitude,
        });
        setViewState({
          longitude: initialLocation?.longitude,
          latitude: initialLocation?.latitude,
        });
      } else {
        setLocation?.({
          longitude: 0,
          latitude: 0,
        });
        setViewState(currentLocation);
      }
      setSearchValue("");
    };

    return (
      <Box>
        <Button
          onClick={handleReset}
          variant="contained"
          color="warning"
          sx={{ padding: 0.5, fontSize: 10 }}
        >
          Reset
        </Button>
      </Box>
    );
  }

  function MapSearchBox() {
    const handleRetrieve = (res: any) => {
      const feature = res?.features[0];
      if (feature) {
        const [lng, lat] = feature?.geometry?.coordinates;
        const searchKeyWord = res?.features[0]?.properties.full_address;
        setSearchValue(searchKeyWord);
        setViewState({
          longitude: lng,
          latitude: lat,
        });
      }
    };

    return (
      <Box margin={1}>
        <Grid container>
          <Grid md={4} xs={12}>
            <SearchBox
              value={searchValue}
              accessToken={GEO_LOCATION_TOKEN || ""}
              map={mapRef?.current?.getMap()}
              onRetrieve={handleRetrieve}
            />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <>
      {!viewLocationsList && (
        <Box sx={{ ...styles.title }}>
          <H4 fontWeight={"bold"}> Geo Locator </H4>
          <Button
            onClick={handleCloseDrawer}
            color="inherit"
            variant={"contained"}
            sx={{ ...styles.closeButton }}
          >
            <CloseIcon />
          </Button>
        </Box>
      )}
      <Box sx={{ width: "100%", height: "90vh" }}>
        <Map
          ref={mapRef}
          mapboxAccessToken={GEO_LOCATION_TOKEN}
          {...viewState}
          initialViewState={{
            longitude: viewState?.longitude,
            latitude: viewState?.latitude,
            zoom: zoom || viewState?.zoom || 8,
          }}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/mapbox/streets-v9"
          onMove={(evt: any) => setViewState(evt.viewState)}
        >
          {viewLocationsList ? (
            <>
              {(locations ?? []).length > 0 && (
                <Source
                  id="dynamic-data"
                  type="geojson"
                  data={locations as any}
                >
                  <Layer {...layerStyle} />
                </Source>
              )}
            </>
          ) : (
            <Source id="my-data" type="geojson" data={geojson}>
              <Layer {...layerStyle} />
            </Source>
          )}

          {!viewLocationsList && viewState ? (
            <Marker
              latitude={viewState?.latitude}
              longitude={viewState?.longitude}
              draggable={editable}
            />
          ) : null}

          {viewLocationsList &&
            (locations ?? []).length > 0 &&
            (locations ?? []).map((point: any, index: number) => (
              <Marker
                key={`point-${index}`}
                longitude={point.longitude}
                latitude={point.latitude}
                anchor="bottom"
              >
                <FlexBox
                  justifyContent={"center"}
                  alignItems={"center"}
                  flexDirection={"column"}
                >
                  <Typography fontSize={16} fontWeight={600}>
                    {point?.title || ""}
                  </Typography>
                  <PlaceIcon color="error" />
                </FlexBox>
              </Marker>
            ))}

          <NavigationControl position="bottom-right" />
          <FullscreenControl />
          <GeolocateControl
            positionOptions={{ enableHighAccuracy: true }}
            trackUserLocation={true}
            showUserLocation={true}
          />
          <ScaleControl />

          {showPopup && editable && (
            <Popup
              longitude={currentLocation?.longitude}
              latitude={currentLocation?.latitude}
              anchor="bottom"
              onClose={() => setShowPopup(false)}
            >
              You are here
            </Popup>
          )}

          {editable && (
            <>
              <MapSearchBox />
              <FlexBox flexDirection={"column"} gap={1} margin={1}>
                <CurrentLocationNavigateButton />
                <SaveLocationButton />
                <ClearCurrentLocationButton />
              </FlexBox>
            </>
          )}
        </Map>
      </Box>
    </>
  );
};

const styles = {
  title: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid #E5EAF2",
    padding: 2,
    position: "sticky",
    backgroundColor: "white",
    heigh: "20vh",
  },
  closeButton: {
    borderRadius: "50%",
    padding: "0 !important",
    width: "2rem",
    height: "2rem",
    minWidth: "2rem",
    backgroundColor: "white",

    "&:hover": {
      backgroundColor: "inherit.main",
      color: "black",
    },
  },
};

export default GeoLocatorMap;
